/**
 * Hatch lines for a zone outline.
 *
 * A classification drawing is read in black and white as often as in colour, so a zone is told apart
 * by its hatch, not only by the colour of its layer (Fig. A.1 uses the same idea). CAD hatching is a
 * whole entity type with pattern definitions; drawing the lines explicitly is simpler, is understood
 * by every reader, and survives a plot to paper.
 */
import type { Outline } from '../drawing/drawing';
import type { Point } from './dxf';

export type Segment = [Point, Point];

/** A drawing nobody can open is worse than a coarse one: a pathological shape stops here. */
const MAX = 20_000;

/**
 * Lines at `angleDeg` spaced `spacing` apart, clipped to the outline. The lines run across the shape
 * in a rotated frame, so the spacing is the perpendicular distance between them whatever the angle.
 */
export function hatchLines(outline: Outline, spacing: number, angleDeg: number): Segment[] {
  if (!(spacing > 0)) return [];
  const a = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  /** Into the frame where hatch lines are horizontal, and back out again. */
  const into = (p: Point): Point => [p[0] * cos + p[1] * sin, -p[0] * sin + p[1] * cos];
  const outOf = (p: Point): Point => [p[0] * cos - p[1] * sin, p[0] * sin + p[1] * cos];

  if (outline.kind === 'circle') {
    const c = into([outline.cu, outline.cv]);
    const r = outline.r;
    const out: Segment[] = [];
    const first = Math.ceil((c[1] - r) / spacing) * spacing;
    for (let v = first; v < c[1] + r; v += spacing) {
      const half = Math.sqrt(Math.max(0, r * r - (v - c[1]) * (v - c[1])));
      if (half < spacing / 50) continue;
      if (out.length >= MAX) return out;
      out.push([outOf([c[0] - half, v]), outOf([c[0] + half, v])]);
    }
    return out;
  }

  const pts = outline.pts.map((p) => into([p[0], p[1]]));
  if (pts.length < 3) return [];
  const vs = pts.map((p) => p[1]);
  const lo = Math.min(...vs);
  const hi = Math.max(...vs);
  const out: Segment[] = [];
  const first = Math.ceil(lo / spacing) * spacing;
  for (let v = first; v < hi; v += spacing) {
    const crossings: number[] = [];
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i]!;
      const q = pts[(i + 1) % pts.length]!;
      // a half-open test, so a vertex exactly on the line is counted once rather than twice
      if ((p[1] <= v && q[1] > v) || (q[1] <= v && p[1] > v)) {
        crossings.push(p[0] + ((v - p[1]) / (q[1] - p[1])) * (q[0] - p[0]));
      }
    }
    crossings.sort((x, y) => x - y);
    for (let i = 0; i + 1 < crossings.length; i += 2) {
      const x0 = crossings[i]!;
      const x1 = crossings[i + 1]!;
      if (x1 - x0 < spacing / 50) continue;
      if (out.length >= MAX) return out;
      out.push([outOf([x0, v]), outOf([x1, v])]);
    }
  }
  return out;
}

/** Whether a point is inside the outline, by the even-odd rule. */
export function inside(outline: Outline, p: Point): boolean {
  if (outline.kind === 'circle') {
    const dx = p[0] - outline.cu;
    const dy = p[1] - outline.cv;
    return dx * dx + dy * dy <= outline.r * outline.r;
  }
  const pts = outline.pts;
  let is = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if ((a[1] > p[1]) !== (b[1] > p[1]) && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) is = !is;
  }
  return is;
}

/**
 * A grid of points inside the outline, for the ring pattern Zone 0 is drawn with. The grid is laid
 * on absolute coordinates, so neighbouring zones line up instead of each starting from its own edge.
 */
export function hatchDots(outline: Outline, spacing: number): Point[] {
  if (!(spacing > 0)) return [];
  const b = bounds(outline);
  const out: Point[] = [];
  const first = (v: number) => Math.ceil(v / spacing) * spacing;
  for (let x = first(b.minU); x <= b.maxU; x += spacing) {
    for (let y = first(b.minV); y <= b.maxV; y += spacing) {
      if (out.length >= MAX) return out;
      if (inside(outline, [x, y])) out.push([x, y]);
    }
  }
  return out;
}

function bounds(outline: Outline): { minU: number; maxU: number; minV: number; maxV: number } {
  if (outline.kind === 'circle') {
    return { minU: outline.cu - outline.r, maxU: outline.cu + outline.r, minV: outline.cv - outline.r, maxV: outline.cv + outline.r };
  }
  const us = outline.pts.map((p) => p[0]);
  const vs = outline.pts.map((p) => p[1]);
  return { minU: Math.min(...us), maxU: Math.max(...us), minV: Math.min(...vs), maxV: Math.max(...vs) };
}
